/**
 * The free-run countdown, shared by the shell banner and the plan screen.
 *
 * Its own module because a component file that also exports constants breaks fast refresh
 * (react-refresh/only-export-components) — and because both surfaces must count the same
 * way: two copies of a "days remaining" rule would let the banner and the screen disagree
 * in front of the same person.
 */

/** What every charge point asks (owner 2026-09-10): a billable case is agreed to where it is added.
 *  Inside the free period it also says nothing is charged yet (ToS 9.4), which the bare sentence
 *  left a free-plan practice to guess. `freePeriodEndsAt` is the staff-only `trialEndsAt` of the
 *  practice the case is added to; null, or a date already past, leaves the bare sentence. */
export function billableCaseAcknowledgement(freePeriodEndsAt: number | null, now: number): string {
  const base = 'I agree this adds a billable case to my practice’s plan.';
  if (freePeriodEndsAt === null || freePeriodEndsAt <= now) return base;
  const until = new Date(freePeriodEndsAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  return `${base} Your free period runs until ${until}, so it is not charged before then.`;
}

/** Whole days from `now` until `endsAt`, floored; negative once it has passed. */
export function trialDaysRemaining(endsAt: number, now: number): number {
  return Math.floor((endsAt - now) / (24 * 60 * 60 * 1000));
}

/** How close the wall must be before the shell says anything. Mirrors the worker's
 *  TRIAL_ENDING_REMINDER_WINDOW_MS, so banner and email appear together. */
export const TRIAL_WARNING_DAYS = 14;

/** Mirror READ_ONLY_AFTER_MS / PLAN_ONLY_AFTER_MS (src/lib/capacity.ts) in whole days — the
 *  ladder an owing practice is on (ToS 9.5, terms-2.4). Two copies for the same reason
 *  TRIAL_WARNING_DAYS is: frontend and backend are separate bundles. */
export const READ_ONLY_AFTER_DAYS = 7;
export const PLAN_ONLY_AFTER_DAYS = 14;

/** What happens if nothing is done, in the owner-approved wording sendOwingNoticeTx already
 *  emails on day 0 (src/repositories/practice-subscriptions.ts) — said here too so the free
 *  run's end is never a surprise wall (TF-0022). Only true while still short of
 *  READ_ONLY_AFTER_DAYS; a caller past that point is already living the first consequence,
 *  not heading for it. */
export const FREE_PERIOD_LAPSE_CONSEQUENCE =
  `If a plan is not in place within ${String(READ_ONLY_AFTER_DAYS)} days your practice becomes `
  + `read-only, and within ${String(PLAN_ONLY_AFTER_DAYS)} days it is restricted to its plan page.`;

/** '' once the practice is genuinely read-only or beyond (the present-tense
 *  SubscriptionReadOnlyBanner/SubscriptionEndedBanner own that message instead) OR while it
 *  holds no case/agreement slots at all — `practiceOwing` (src/lib/capacity.ts) never fires
 *  at heldSlots===0 regardless of elapsed time, so promising the ladder there would be false
 *  (prod adversarial review, TF-0022). Otherwise FREE_PERIOD_LAPSE_CONSEQUENCE with a leading
 *  space. The ONE home for the boundary check, so three call sites cannot each re-type it
 *  slightly wrong — `trialDaysRemaining` floors toward -Infinity, so
 *  daysRemaining === -READ_ONLY_AFTER_DAYS covers the last, not-yet-read-only, hours of day 6
 *  too: `>=`, not `>`. */
export function freePeriodLapseConsequence(daysRemaining: number, heldSlots: number): string {
  return heldSlots > 0 && daysRemaining >= -READ_ONLY_AFTER_DAYS ? ` ${FREE_PERIOD_LAPSE_CONSEQUENCE}` : '';
}
